import React from 'react';
import { Card, CardContent } from './ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * Enhanced inventory metric card component for ERP-style displays
 * Supports icons, subtitles, and various color schemes
 */
const InventoryMetricCard = React.memo(({
  title,
  value,
  subtitle,
  icon,
  onClick,
  color = 'text-primary',
  bgColor = 'bg-muted',
  borderColor = 'border',
  tooltipTitle,
  isClickable = true
}) => {
  const CardComponent = (
    <Card
      className={`${bgColor} ${borderColor} border h-full transition-all duration-200 ${
        isClickable
          ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md'
          : ''
      }`}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      } : undefined}
    >
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Icon and Title Row */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {title}
            </span>
            {icon && (
              <span className={`${color} opacity-70`}>
                {React.cloneElement(icon, { className: 'h-4 w-4' })}
              </span>
            )}
          </div>

          {/* Value */}
          <p
            className={`text-3xl font-bold tabular-nums leading-none ${color}`}
            aria-label={`${title}: ${value}`}
          >
            {value}
          </p>

          {/* Subtitle */}
          {subtitle && (
            <p className="text-xs text-muted-foreground leading-tight line-clamp-2 min-h-[2.6em]">
              {subtitle}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (isClickable && tooltipTitle) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {CardComponent}
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{tooltipTitle}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return CardComponent;
});

InventoryMetricCard.displayName = 'InventoryMetricCard';

export default InventoryMetricCard;
